import axios from 'axios';
import { auth } from '../config/firebase';
import { getTenantRuntime, getTenantGeneration } from '../config/tenant';
export const PLATFORM_GATEWAY_URL = 'https://api-gateway-422230384703.europe-west3.run.app';
export function getApiBase(): string { return getTenantRuntime().apiBase; }
export function apiUrl(path: string): string { return `${getApiBase()}${path}`; }
export function serviceUrl(service: 'businessServices' | 'voiceTools' | 'microsoftConnect', path = ''): string {
  const runtime = getTenantRuntime();
  const url = runtime.legacy ? runtime.serviceUrls?.[service] : runtime.apiBase;
  if (!url) throw new Error(`Konfiguration unvollständig (${runtime.tenantId})`);
  return `${url}${path}`;
}
export const http = axios.create({ timeout: 30000, headers: { 'Content-Type': 'application/json' } });
http.interceptors.request.use(async config => {
  const runtime = getTenantRuntime();
  const generation = getTenantGeneration();
  const request = config as typeof config & { tenantGeneration?: number };
  if (request.tenantGeneration !== undefined && request.tenantGeneration !== generation) throw new Error('Mandant wurde während der Anfrage gewechselt. Bitte erneut versuchen.');
  request.tenantGeneration = generation;
  if (auth.currentUser) config.headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
  if (generation !== getTenantGeneration()) throw new Error('Mandant wurde während der Anfrage gewechselt. Bitte erneut versuchen.');
  config.baseURL = runtime.apiBase;
  return config;
});
// Opt-in retry for transient failures (network, 5xx, 429), as the former ApiService did for the task service:
// up to 3 retries, Retry-After or exponential backoff (1 s, 2 s, 4 s; max 30 s). Other callers are unchanged.
declare module 'axios' { interface AxiosRequestConfig { retryOnTransient?: boolean; } }
const MAX_TRANSIENT_RETRIES = 3;
http.interceptors.response.use(response => response, async error => {
  const config = error.config as (typeof error.config & { _transientAttempt?: number }) | undefined;
  const status: number | undefined = error.response?.status;
  // Timeouts are not retried (as before): the server may already have stored a create (no duplicates).
  const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
  const transient = !timedOut && (!error.response || status === 429 || (status !== undefined && status >= 500));
  if (!config?.retryOnTransient || !transient || axios.isCancel(error)) throw error;
  const attempt = config._transientAttempt ?? 0;
  if (attempt >= MAX_TRANSIENT_RETRIES) throw error;
  config._transientAttempt = attempt + 1;
  const retryAfter = Number(error.response?.headers?.['retry-after']);
  const delay = status === 429 && Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : Math.min(1000 * 2 ** attempt, 30000);
  await new Promise(resolve => setTimeout(resolve, delay));
  return http(config);
});
let refreshing: Promise<string> | null = null;
http.interceptors.response.use(response => response, async error => {
  const config = error.config;
  if (error.response?.status !== 401 || !config || config._retry) throw error;
  if (!auth.currentUser) { window.location.href = '/login'; throw error; }
  if (config.tenantGeneration !== getTenantGeneration()) throw error;
  config._retry = true;
  refreshing ??= auth.currentUser.getIdToken(true).finally(() => { refreshing = null; });
  try { config.headers.Authorization = `Bearer ${await refreshing}`; }
  catch (refreshError) { window.location.href = '/login'; throw refreshError; }
  return http(config);
});
export async function getCurrentAuthToken() { return auth.currentUser ? auth.currentUser.getIdToken() : null; }
export async function switchActiveTenant(tenantId: string): Promise<void> {
  const token = await getCurrentAuthToken();
  if (!token) throw new Error('Bitte erneut anmelden.');
  const response = await fetch(`${PLATFORM_GATEWAY_URL}/api/me/active-tenant`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantId }) });
  const payload = await response.json();
  if (!response.ok || !payload.success) throw new Error(payload.message || 'Mandantenwechsel nicht möglich');
}

// Types aligned with OpenAPI schemas
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  details?: string[];
  correlationId?: string;
  timestamp?: string;
  duplicate?: boolean;
}
